import React from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, Download, Send, CheckCircle } from 'lucide-react';
import { API_URL } from '../utils/apiConfig';
import axios from 'axios';

// Apple-inspired color palette matching dashboard
const colors = {
  accent: '#1e40af',
  accentHover: '#1e3a8a',
  accentLight: 'rgba(30, 64, 175, 0.1)',
  white: '#ffffff',
  gray50: '#f9f9f9',
  gray100: '#f3f3f3',
  gray200: '#e6e6e6',
  gray300: '#d1d1d1',
  gray500: '#8e8e8e',
  gray700: '#636363',
  gray900: '#333333',
  success: 'rgb(52, 199, 89)',
  successLight: 'rgba(52, 199, 89, 0.1)',
};

const fontStyles = {
  fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
  heading: {
    fontWeight: 600,
    letterSpacing: '-0.03em',
  },
  body: {
    fontWeight: 400,
    letterSpacing: '-0.01em',
  },
  medium: {
    fontWeight: 500,
  },
};

const SignatureSentPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const handleDownload = async () => {
    try {
      let fileUrl = `${API_URL}/documents/${id}/download`;
      const headers = {};
      
      if (token) {
        fileUrl += `?token=${encodeURIComponent(token)}`;
      } else {
        const authToken = localStorage.getItem('token');
        if (!authToken) {
          alert('You are not authenticated to download this document.');
          return;
        }
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const response = await axios.get(fileUrl, {
        responseType: 'blob',
        headers
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `document-${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(blobUrl);
      
    } catch (err) {
      console.error('Error downloading document:', err);
      alert('Failed to download document.');
    }
  };

  return (
    <div 
      className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden"
      style={{
        background: `radial-gradient(circle at 70% 20%, ${colors.accentLight} 0%, rgba(255, 255, 255, 0) 25%)`,
        fontFamily: fontStyles.fontFamily,
      }}
    >
      {/* SignPact Logo */}
      <img 
        src="/6.png" 
        alt="SignPact Logo" 
        className="absolute top-8 left-1/2 transform -translate-x-1/2 h-12"
        style={{
          filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))'
        }}
      />

      {/* Success Card */}
      <div 
        className="bg-white rounded-3xl border p-12 max-w-lg w-full mx-4 relative"
        style={{
          borderColor: colors.gray200,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.12), 0 10px 20px -5px rgba(0, 0, 0, 0.08)',
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.9))',
          backdropFilter: 'blur(20px)',
        }}
      >
        {/* Paper plane animation container */}
        <div className="relative mb-10">
          <div 
            className="absolute inset-0 bg-gradient-to-b from-indigo-50 to-transparent rounded-full opacity-50"
            style={{
              transform: 'scale(1.2)',
              filter: 'blur(20px)'
            }}
          />
          <Send 
            className="w-24 h-24 mx-auto text-indigo-600 rotate-[30deg] transform transition-transform duration-700 hover:rotate-[45deg]"
            strokeWidth={1.25}
            style={{
              filter: 'drop-shadow(0 8px 16px rgba(30, 64, 175, 0.2))'
            }}
          />
        </div>

        {/* Success checkmark */}
        <div className="flex items-center justify-center mb-6">
          <div 
            className="w-12 h-12 rounded-full flex items-center justify-center"
            style={{
              background: colors.successLight,
              color: colors.success
            }}
          >
            <CheckCircle size={24} />
          </div>
        </div>

        <h1 
          className="text-3xl md:text-4xl font-bold text-center mb-4"
          style={{
            color: colors.gray900,
            ...fontStyles.heading
          }}
        >
          Your signature was sent
        </h1>
        
        <p 
          className="text-center mb-10"
          style={{
            color: colors.gray700,
            ...fontStyles.body
          }}
        >
          We'll let you know once all parties have executed their agreements.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <button
            onClick={handleDownload}
            className="flex-1 flex items-center justify-center transition-all duration-300"
            style={{
              background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentHover} 100%)`,
              color: colors.white,
              padding: '14px 28px',
              borderRadius: '12px',
              boxShadow: '0 8px 25px rgba(30, 64, 175, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
              ...fontStyles.medium,
              fontSize: '15px'
            }}
            onMouseEnter={e => {
              e.target.style.transform = 'translateY(-2px) scale(1.02)';
              e.target.style.boxShadow = '0 12px 35px rgba(30, 64, 175, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)';
            }}
            onMouseLeave={e => {
              e.target.style.transform = 'translateY(0) scale(1)';
              e.target.style.boxShadow = '0 8px 25px rgba(30, 64, 175, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
            }}
          >
            <Download className="w-5 h-5 mr-2" />
            Download Document
          </button>

          <Link
            to="/dashboard"
            className="flex items-center justify-center transition-all duration-300"
            style={{
              background: colors.white,
              color: colors.gray700,
              padding: '14px 28px',
              borderRadius: '12px',
              border: `1px solid ${colors.gray200}`,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
              ...fontStyles.medium,
              fontSize: '15px'
            }}
            onMouseEnter={e => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 8px 20px rgba(0, 0, 0, 0.08)';
              e.target.style.borderColor = colors.gray300;
            }}
            onMouseLeave={e => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.05)';
              e.target.style.borderColor = colors.gray200;
            }}
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </Link>
        </div>
      </div>

      <footer 
        className="absolute bottom-4 text-sm"
        style={{
          color: colors.gray500,
          ...fontStyles.body
        }}
      >
        &copy; {new Date().getFullYear()} SignPact
      </footer>
    </div>
  );
};

export default SignatureSentPage; 